export interface VerificationJob {
  id: string;
  status: "queued" | "running" | "succeeded" | "failed" | "cancelled";
  stage: "preparing" | "inputs" | "sources" | "orders" | "totals" | "history" | "saving" | null;
  queuedAt: string;
  startedAt: string | null;
  finishedAt: string | null;
  reportRanAt: string | null;
  needsAttention: boolean;
}

export function verificationIsRunning(job: VerificationJob | null | undefined): boolean {
  return job?.status === "queued" || job?.status === "running";
}

export function verificationBlocksApproval(job: VerificationJob | null | undefined, reportRanAt: string | null): boolean {
  if (!job) return false;
  if (verificationIsRunning(job)) return true;
  return (job.status === "failed" || job.status === "cancelled")
    && (!reportRanAt || Date.parse(job.queuedAt) > Date.parse(reportRanAt));
}

const STAGE_LABELS: Record<NonNullable<VerificationJob["stage"]>, string> = {
  preparing: "Finding the orders and products to check",
  inputs: "Checking the product costs and basic calculations",
  sources: "Checking the imported records",
  orders: "Checking individual orders",
  totals: "Comparing totals across the dashboard",
  history: "Checking for gaps in the history",
  saving: "Saving the results",
};

export function VerificationRunStatus({ job, reportRanAt, onRetry }: {
  job: VerificationJob | null | undefined;
  reportRanAt: string | null;
  onRetry?: () => void;
}): React.ReactElement | null {
  if (!job) return null;
  const active = verificationIsRunning(job);
  const failed = job.status === "failed" || job.status === "cancelled";
  // A later nightly report supersedes the old job receipt, including failures.
  if (!active && reportRanAt && Date.parse(reportRanAt) > Date.parse(job.finishedAt ?? job.queuedAt)) return null;
  const title = active ? "Checks are in progress" : failed ? "The last check stopped" : "Checks finished";
  const detail = job.needsAttention
    ? "The background worker is taking longer than expected. This is not a completed check. Your last saved results are still below."
    : job.status === "queued"
      ? "Your check is saved and waiting to start."
      : job.status === "running"
        ? `${STAGE_LABELS[job.stage ?? "preparing"]}.`
        : failed
          ? "The check did not finish. Your last saved results are still below. Run the checks again to retry."
          : "The new results are saved below. Any remaining problems still need attention before approval.";
  return (
    <section role="status" aria-live="polite" style={{
      margin: "0 0 14px", padding: "13px 15px", borderRadius: 10,
      background: "var(--surface)", border: `1px solid ${failed || job.needsAttention ? "var(--warn, #E5A83B)" : "var(--border)"}`,
    }}>
      <strong style={{ color: "var(--text)", fontSize: 13 }}>{title}</strong>
      <p style={{ margin: "5px 0 0", color: "var(--body)", fontSize: 12.5, lineHeight: 1.5 }}>{detail}</p>
      {active && <p style={{ margin: "5px 0 0", color: "var(--body)", fontSize: 12.5, lineHeight: 1.5 }}>
        A full check can take several minutes. You can leave this page and come back.
      </p>}
      {job.needsAttention && job.status === "queued" && onRetry && <button type="button" onClick={onRetry} style={{
        marginTop: 9, padding: "6px 10px", borderRadius: 6, background: "var(--surface)",
        color: "var(--text)", border: "1px solid var(--border)", cursor: "pointer",
      }}>Retry starting this check</button>}
      {job.finishedAt && !active && <p style={{ margin: "5px 0 0", color: "var(--body-faint)", fontSize: 11.5 }}>
        {failed ? "Stopped" : "Finished"} {new Date(job.finishedAt).toLocaleString()}
      </p>}
    </section>
  );
}
